import React, { useState, useEffect, useRef } from 'react';
import { Clock, AlertCircle } from 'lucide-react';
import { formatTime, getTimerColor, getTimerStatus } from '../../utils/timerManager';
import { playSound } from '../../utils/soundManager';

const Timer = ({ 
  totalTime, 
  warningTime, 
  onTimeUp, 
  isPaused = false,
  showProgress = true 
}) => {
  const [timeRemaining, setTimeRemaining] = useState(totalTime);
  const [status, setStatus] = useState('safe');
  const intervalRef = useRef(null);
  const hasPlayedWarning = useRef(false);

  useEffect(() => {
    setTimeRemaining(totalTime);
    hasPlayedWarning.current = false;
  }, [totalTime]);

  useEffect(() => {
    if (isPaused) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      return;
    }

    intervalRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        const newTime = prev - 1;
        
        // Update status
        const newStatus = getTimerStatus(newTime, totalTime, warningTime);
        setStatus(newStatus);
        
        // Play warning sound
        if (newStatus === 'warning' && !hasPlayedWarning.current) {
          playSound('click');
          hasPlayedWarning.current = true;
        }
        
        // Play critical sound
        if (newStatus === 'critical') {
          playSound('click');
        }
        
        // Time's up
        if (newTime <= 0) {
          clearInterval(intervalRef.current);
          playSound('incorrect');
          if (onTimeUp) {
            onTimeUp();
          }
          return 0;
        }
        
        return newTime;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPaused, totalTime, warningTime, onTimeUp]);

  const percentage = (timeRemaining / totalTime) * 100;
  const color = getTimerColor(timeRemaining, totalTime, warningTime);

  return (
    <div className={`timer-container ${status}`}>
      <div className="timer-display">
        <div className="timer-icon" style={{ color }}>
          {status === 'critical' || status === 'warning' ? (
            <AlertCircle size={24} className="animate-pulse" />
          ) : (
            <Clock size={24} />
          )}
        </div>
        
        <div className="timer-time" style={{ color }}>
          {formatTime(timeRemaining)}
        </div>
        
        {status === 'critical' && (
          <div className="timer-warning animate-shake">
            Hurry!
          </div>
        )}
      </div>

      {showProgress && (
        <div className="timer-progress-bar">
          <div 
            className="timer-progress-fill"
            style={{ 
              width: `${percentage}%`,
              backgroundColor: color,
              transition: 'width 1s linear, background-color 0.3s ease'
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Timer;
